import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Json, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { PermAction, PermOptions, TableWithPermissions } from '@/types/base/permissions'
import Authuser, { evaluatePermissions } from '@/types/base/authuser'
import { Role } from "@/types/base/authuser"
import { isAidonAdmin, isTenantAdmin } from './tenants'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { getWorkspaceById } from './workspaces'
import { Pair } from '@/types/base/pair'

const logger = createIsomorphicLogger('db.profile')

interface ProfileMetadata {
  wix_contact_id?: string
}

export const getProfileById = async (supabase: SupabaseClient<Database>, profileId: string) => {
	// eslint-disable-next-line prefer-const
  let { data: profile, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", profileId)
    .single()

	if (error) {
    logError(logger, error, 'Error in fetching Profile by ID', { profileId })
		profile = null
	}
	else if (!profile) {
		logger.warn(`Attempt to fetch Profile by ID resulted in no content found`, { profileId })
	}

	return profile
}

export const getProfileByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
  const { data: profile, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Error in fetching Profile by user ID', { userId })
    throw new Error(error.message)
  }
	else if (!profile) {
		logger.warn(`Attempt to fetch Profile by user ID resulted in no content found`, { userId })
	}

  return profile
}

export const getProfiles = async (supabase: SupabaseClient<Database>, ids: string[]) => {
	const { data, error } = await supabase
		.from('profiles')
		.select('*')
		.in('id', ids)

	if (error) throw error
	return data
}

export const getProfileByUserName = async (supabase: SupabaseClient<Database>, userName: string) => {
	// eslint-disable-next-line prefer-const
  let { data: profile, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("username", userName)
    .maybeSingle()

	if (error) {
    logError(logger, error, 'Error in fetching Profile by username', { userName })
		profile = null
	}
	else if (!profile) {
		logger.warn(`Attempt to fetch Profile by username resulted in no content found`, { userName })
	}

  return profile
}

export const getProfilesByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
  const { data: profiles, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", userId)

  if (!profiles) {
    logError(logger, error, 'Error in fetching Profiles by user ID', { userId })
    throw new Error(error.message)
  }


  return profiles
}

export const getProfileTenantIdByUserId = async (
	supabase: SupabaseClient<Database>,
	userId: string
): Promise<Pair<Tables<"profiles">, string | null>> => {
	const profile = await getProfileByUserId(supabase, userId)

	if (!profile) {
		throw new Error("Profile not found")
	}

	const { data: tenantId, error } = await supabase.rpc('user_tenant_id', { user_id: userId })

	if (error) {
		throw error
	}

	return new Pair(profile, tenantId)
}

// select g.*, gu.*, p.username from profiles p
// join tenant_users tu on p.id = tu.tenant_user_id
// join group_users gu on gu.group_user_id = tu.id
// join groups g on gu.group_id = g.id
// where p.user_id = '01a2b02a-f3f7-4deb-aed0-0340d126f0c7'
export const getProfileGroupsByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
  const { data: profileGroups, error } = await supabase
    .from("profiles")
    .select(
      `
      id,
      username,
      tenant_users(groups:group_users(...groups(*)))
    `
    )
    .eq("user_id", userId)
  if (!profileGroups) {
    logError(logger, error, 'Error in fetching Profile groups by user ID', { userId })
    throw new Error(error.message)
  }

  return profileGroups
}

export const getAllProfiles = async (supabase: SupabaseClient<Database>) => {
  const { data: profiles, error } = await supabase
    .from("profiles")
    .select("*")

  if (!profiles) {
    logError(logger, error, 'Failed to fetch all Profiles')
    throw new Error(error.message)
  }

  return profiles
}

export const getTenancyProfiles = async (supabase: SupabaseClient<Database>, tenantId: string) => {
  const { data: profiles, error } = await supabase
    .from("profile_tenant_vw")
    .select("id")
		.eq('tenant_id', tenantId)

  if (!profiles) {
    logError(logger, error, 'Failed to fetch tenaant Profiles')
    throw new Error(error.message)
  }

	return getProfiles(supabase, profiles.map(p => p.id).filter((id): id is string => id !== null))
}

export const getAllProfilesWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'profiles'>[]> => {
	const allProfiles = await getAllProfiles(supabase)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'profiles'>[] = []

	for (const profile of allProfiles) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "profiles", context: "ANY", uuid: profile.id, ws: workspace})

		result.push({ ...profile, permissions: perms })
	}

  return result
}

export const createProfile = async (supabase: SupabaseClient<Database>, profile: TablesInsert<"profiles">) => {
  const { data: createdProfile, error } = await supabase
    .from("profiles")
    .insert([profile])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Error in creating Profile', { profile })
    throw new Error(error.message)
  }

  logger.info('Profile created successfully', { profileId: createdProfile.id })
  return createdProfile
}

export const updateProfile = async (
	supabase: SupabaseClient<Database>,
  profileId: string,
  profile: TablesUpdate<"profiles">
) => {
  // Remove 'name' key if present since this is a generated column
  const { name: _name, ...profileWithoutName } = profile

  const { data: updatedProfile, error } = await supabase
    .from("profiles")
    .update(profileWithoutName)
    .eq("id", profileId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Error in updating Profile', { profileId, profile })
    throw new Error(error.message)
  }

	logger.info('Profile updated successfully', { profileId: profileId })
  return updatedProfile
}

export const deleteProfile = async (supabase: SupabaseClient<Database>, profileId: string) => {
  const { error } = await supabase.from("profiles").delete().eq("id", profileId)

  if (error) {
    logError(logger, error, 'Error in deleting Profile', { profileId })
    throw new Error(error.message)
  }

  return true
}

export const getWixContactId = (profile: TablesUpdate<"profiles"> | null) => {
	const prMeta = profile?.metadata as ProfileMetadata | undefined;
	return prMeta?.wix_contact_id
}

export const setMetadataWixContactId = (
	metadata: Json,
	wixContactId: string
) => {
	const prMeta: ProfileMetadata = metadata ? { ...metadata as ProfileMetadata } : {}
  prMeta.wix_contact_id = wixContactId;
	return prMeta as Json
}

export const profileCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.context == 'IMPORT' && await isAidonAdmin(supabase, authuser)) {
		return true
	}

	if ((options.context == 'SHARE_KEYS' || options.context == 'VIEW_BILLING') &&
			authuser.isAuthorizedForRole(Role.admin)) {
		return true
	}

	if (options.context == 'EXPORT' || options.context == 'IMPEXP') {
		if (authuser.isAuthorizedForRole(Role.admin) || (options.ws && authuser.isOwnWorkspace(options.ws))) {
			return true
		}
	}

	switch (options.action) {
		case PermAction.delete:  //no one can delete from here
			return false
		//fall through
		case PermAction.view:
		case PermAction.edit:
		case PermAction.save:
			return isTenantAdmin(authuser)
			// if (options.ws && authuser.isOwnWorkspace(options.ws)) {
			// 	return true
			// }
		default:  //includes create
			return isAidonAdmin(supabase, authuser)
	}
}
